/**
 * Allotment of Shares — Bonus Issue (M32203) mapper.
 * 공유 상태(BonusState) → [[변수]] payload. OTC(allotmentOtcMapper) 미러링.
 *
 * Bonus 는 8문서가 값을 공유하므로 병합 payload 하나를 만들어 모든 템플릿에 전달한다
 * (템플릿은 자기가 쓰는 [[변수]] 만 치환, 나머지는 무시).
 *
 * 반복부:
 *   [[if allottees]] … allottee_name / allottee_address / allottee_shares / allottee_id_no /
 *                       allottee_share_type / allottee_amount [[/if allottees]]
 *   [[if shares]]    … share_type / share_details / share_number / share_price_per_share / share_total_issued [[/if shares]] (Section 78)
 */
import { BONUS_ALLOTTEE_MAX, BONUS_SHARE_CLASS_MAX } from '../components/bonus/bonusFields';

export interface BonusState {
  values: Record<string, string>;    // 스칼라 + flat 반복행 키(allottee_name_${n} 등)
  flags: Record<string, boolean>;    // 조건 토글
  choices: Record<string, string>;   // resolution_basis / statement_resolution_basis
  allotteeCount: number;
  shareClassCount: number;
  /** [[Signers]] — 문서(서명영역) varKey 별 선택된 서명자. prepare-documents 의 signers 로 전달. */
  signers: Record<string, any[]>;
}

export function emptyBonusState(): BonusState {
  return {
    values: {},
    flags: { has_constitution: true },
    choices: { resolution_basis: 'constitution', statement_resolution_basis: 'reserve' },
    allotteeCount: 2,
    shareClassCount: 1,
    signers: {},
  };
}

const num = (v: any) => String(v ?? '').trim();

/** flat allottee_*_${n} → [[if allottees]] 배열. 빈 행은 스킵, allottee_no 자동 부여.
 *  Statement 템플릿 키: allottee_shares_otherwise / allottee_amount_capitalized 도 함께 출력. */
export function buildAllottees(state: BonusState): Record<string, string>[] {
  const v = state.values || {};
  const price = Number(String(v.share_price ?? '').replace(/[^0-9.]/g, '')) || 0;
  const max = Math.min(Math.max(1, state.allotteeCount || 1), BONUS_ALLOTTEE_MAX);
  const rows: Record<string, string>[] = [];
  let no = 0;
  for (let n = 1; n <= max; n++) {
    const name = num(v[`allottee_name_${n}`]);
    const address = num(v[`allottee_address_${n}`]);
    const shares = num(v[`allottee_shares_otherwise_${n}`]) || num(v[`allottee_shares_${n}`]);
    const idNo = num(v[`allottee_id_no_${n}`]);
    let amount = num(v[`allottee_amount_capitalized_${n}`]) || num(v[`allottee_amount_${n}`]);
    const shareType = num(v[`allottee_share_type_${n}`]) || 'Ordinary';
    if (!name && !address && !shares && !idNo && !amount) continue;
    // Amount Capitalized 비어 있으면 shares × share_price 로 보강
    if (!amount && shares && price) {
      amount = String(Number(String(shares).replace(/[^0-9.]/g, '') || 0) * price);
    }
    no += 1;
    rows.push({
      allottee_no: String(no),
      allottee_name: name,
      'allottee.name': name,
      allottee_address: address,
      'allottee.address': address,
      allottee_id_no: idNo,
      allottee_shares: shares,
      allottee_share_count: shares,
      'allottee.shares': shares,
      // Statement (Bonus) Word 템플릿 정본 키
      allottee_shares_otherwise: shares,
      allottee_share_type: shareType,
      allottee_amount: amount,
      allottee_amount_capitalized: amount,
      // Register Detail / hasLinkedContact 폴백
      contactCd: num(v[`allottee_contact_cd_${n}`]),
      allottee_contact_cd: num(v[`allottee_contact_cd_${n}`]),
    });
  }
  return rows;
}

/** flat share_class_*_${n} → [[if shares]] 배열 (Section 78). */
export function buildShareClasses(state: BonusState): Record<string, string>[] {
  const v = state.values || {};
  const max = Math.min(Math.max(1, state.shareClassCount || 1), BONUS_SHARE_CLASS_MAX);
  const rows: Record<string, string>[] = [];
  for (let n = 1; n <= max; n++) {
    const type = num(v[`share_class_type_${n}`]);
    const details = num(v[`share_class_details_${n}`]);
    const number = num(v[`share_class_number_${n}`]);
    const price = num(v[`share_class_price_${n}`]);
    let total = num(v[`share_class_total_${n}`]);
    if (!type && !details && !number && !price && !total) continue;
    // Total Issued 비어 있으면 No. of Shares × Price / Share 로 보강
    if (!total && number && price) {
      const nNum = Number(String(number).replace(/[^0-9.]/g, '')) || 0;
      const pNum = Number(String(price).replace(/[^0-9.]/g, '')) || 0;
      if (nNum && pNum) total = String(nNum * pNum);
    }
    rows.push({
      share_type: type,
      share_details: details,
      share_number: number,
      share_price_per_share: price,
      share_total_issued: total,
    });
  }
  return rows;
}

/** 조건 토글 → 템플릿 [[if X]] 용. AllotmentShares 와 동일하게 truthy 는 'true', falsy 는 '' .
 *   (엔진은 boolean 도 지원하지만 Word 치환 경로에서 문자열 'true' 가 검증된 패턴.) */
export function expandFlags(state: BonusState): Record<string, string | boolean> {
  const f = state.flags || {};
  const c = state.choices || {};
  const basis = c.resolution_basis || 'constitution';
  const stBasis = c.statement_resolution_basis || 'reserve';
  const on = (v: boolean) => (v ? 'true' : '');
  const isTrustee = !!f.is_trustee;
  return {
    has_constitution: f.has_constitution !== false,
    is_multiple_member: on(!!f.is_multiple_member),
    is_sole_member: on(!f.is_multiple_member),
    is_multiple_director_company: on(!!f.is_multiple_director_company),
    is_sole_director_company: on(!f.is_multiple_director_company),
    grant_of_rights: on(!!f.grant_of_rights),
    has_conditions: on(!!f.has_conditions),
    is_politician: on(!!f.is_politician),
    is_pep_connected: on(!!f.is_pep_connected),
    holds_govt_position: on(!!f.holds_govt_position),
    // Trustee / Beneficial Owner — Individual: [[if is_trustee]] / Corporate alias: [[if has_bo]]
    is_trustee: on(isTrustee),
    has_bo: on(isTrustee),
    clause_2_applicable: on(!!f.clause_2_applicable),
    clause_3_applicable: on(!!f.clause_3_applicable),
    resolution_basis_constitution: basis === 'constitution',
    resolution_basis_articles: basis === 'articles',
    resolution_basis_third_schedule: basis === 'third_schedule',
    // Statement 템플릿: [[if resolution_basis_dividend]] / [[if resolution_basis_reserve]]
    //   (BON01 의 constitution/articles 와 다른 축 — statement_resolution_basis 값)
    resolution_basis_dividend: on(stBasis === 'dividend'),
    resolution_basis_reserve: on(stBasis === 'reserve'),
    statement_resolution_basis_dividend: stBasis === 'dividend',
    statement_resolution_basis_reserve: stBasis === 'reserve',
  };
}

/** 병합 payload — 모든 템플릿 공용. */
export function buildBonusVariables(state: BonusState): Record<string, any> {
  const out: Record<string, any> = { ...(state.values || {}) };
  Object.assign(out, expandFlags(state));
  const allottees = buildAllottees(state);
  out.allottees = allottees;
  out.shares = buildShareClasses(state);
  // Edit UI 가 루프 키를 스칼라로도 노출하는 경우 — 첫 행으로 보강.
  const first = allottees[0];
  if (first) {
    if (!num(out.allottee_amount_capitalized)) out.allottee_amount_capitalized = first.allottee_amount_capitalized || '';
    if (!num(out.allottee_shares_otherwise)) out.allottee_shares_otherwise = first.allottee_shares_otherwise || '';
  }
  // Word 템플릿 [[shares_offered]] / [[shares_accepted]] — UI 는 shares_subscribed 중심.
  //   비어 있으면 청약 수량으로 보강 (값 없으면 [[…]] 원문이 문서에 남음).
  const subscribed = num(out.shares_subscribed);
  if (subscribed) {
    if (!num(out.shares_offered)) out.shares_offered = subscribed;
    if (!num(out.shares_accepted)) out.shares_accepted = subscribed;
  }
  return out;
}
